<template>
  <TransitionRoot appear :show="show" as="template">
    <Dialog as="div" @close="close" class="relative z-10">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                Generate Blog Ideas for "{{ keyword?.term }}"
              </DialogTitle>

              <!-- Content Type Selection -->
              <div class="mt-4">
                <label for="contentType" class="block text-sm font-medium text-gray-700">Content Type</label>
                <div class="mt-1 flex space-x-4">
                  <select
                    id="contentType"
                    v-model="selectedContentType"
                    class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="">Select a content type</option>
                    <option value="how-to">How-to Guide</option>
                    <option value="listicle">List Article</option>
                    <option value="tutorial">Tutorial</option>
                    <option value="comparison">Comparison</option>
                    <option value="case-study">Case Study</option>
                    <option value="evergreen">Evergreen Content</option>
                    <option value="review">Review</option>
                  </select>
                  <button
                    @click="generateWithContentType"
                    :disabled="!selectedContentType || loading || localLoading"
                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <template v-if="loading || localLoading">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </template>
                    <template v-else>
                      Generate Ideas
                    </template>
                  </button>
                </div>
                <p class="mt-2 text-sm text-gray-500">
                  Select a content type to generate targeted blog ideas
                </p>
              </div>
                
              <!-- Generated Ideas -->
              <div v-if="ideas?.length" class="mt-6 space-y-4">
                <div v-for="(idea, index) in ideas" :key="index" class="p-4 bg-gray-50 rounded-lg">
                  <div class="flex items-center justify-between">
                    <h4 class="font-medium text-gray-900">{{ idea.title }}</h4>
                    <span class="text-xs font-medium px-2 py-1 rounded-full" 
                      :class="{
                        'bg-blue-100 text-blue-800': idea.contentType === 'how-to',
                        'bg-green-100 text-green-800': idea.contentType === 'listicle',
                        'bg-purple-100 text-purple-800': idea.contentType === 'tutorial',
                        'bg-yellow-100 text-yellow-800': idea.contentType === 'comparison',
                        'bg-red-100 text-red-800': idea.contentType === 'case-study',
                        'bg-indigo-100 text-indigo-800': idea.contentType === 'evergreen',
                        'bg-orange-100 text-orange-800': idea.contentType === 'review',
                        'bg-gray-100 text-gray-800': !idea.contentType || idea.contentType === 'general'
                      }"
                    >
                      {{ idea.contentType || 'General' }}
                    </span>
                  </div>
                  <p class="mt-2 text-sm text-gray-600">{{ idea.description }}</p>
                  <div class="mt-3">
                    <h5 class="text-sm font-medium text-gray-700">Key Points:</h5>
                    <ul class="mt-1 list-disc list-inside text-sm text-gray-600">
                      <li v-for="(point, pointIndex) in idea.keyPoints" :key="pointIndex">
                        {{ point }}
                      </li>
                    </ul>
                  </div>
                  <div class="mt-4 flex justify-end">
                    <button
                      @click="writeArticle(idea)"
                      :disabled="writingArticle === idea.title"
                      class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <template v-if="writingArticle === idea.title">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Writing...
                      </template>
                      <template v-else>
                        Write Article
                      </template>
                    </button>
                  </div>
                </div>
              </div>

              <!-- FAQ and Takeaway Controls -->
              <div class="space-y-4 mt-4">
                <!-- Content Length Selector -->
                <div class="flex items-center space-x-4">
                  <div class="flex items-center space-x-2">
                    <label for="content-length" class="text-sm text-gray-700">Content Length:</label>
                    <select
                      id="content-length"
                      v-model="selectedContentLength"
                      class="mt-1 block w-32 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option value="short">Short (~1,500 words)</option>
                      <option value="medium">Medium (~2,500 words)</option>
                      <option value="long">Long (~4,000 words)</option>
                    </select>
                  </div>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="flex items-center">
                    <input
                      type="checkbox"
                      id="show-faqs"
                      v-model="localShowFaqs"
                      class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    >
                    <label for="show-faqs" class="ml-2 block text-sm text-gray-700">
                      Include FAQs
                    </label>
                  </div>
                  
                  <div v-if="localShowFaqs" class="flex items-center space-x-2">
                    <label for="num-faqs" class="text-sm text-gray-700">Number of FAQs:</label>
                    <select
                      id="num-faqs"
                      v-model="localNumFaqs"
                      class="mt-1 block w-20 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
                    </select>
                  </div>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="flex items-center">
                    <input
                      type="checkbox"
                      id="show-takeaways"
                      v-model="localShowTakeaways"
                      class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    >
                    <label for="show-takeaways" class="ml-2 block text-sm text-gray-700">
                      Include Key Takeaways
                    </label>
                  </div>
                  
                  <div v-if="localShowTakeaways" class="flex items-center space-x-2">
                    <label for="num-takeaways" class="text-sm text-gray-700">Number of Takeaways:</label>
                    <select
                      id="num-takeaways"
                      v-model="localNumTakeaways"
                      class="mt-1 block w-20 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    >
                      <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="mt-6 flex justify-end">
                <button
                  @click="close"
                  class="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useOpenAIStore } from '../stores/openai'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

const props = defineProps({
  show: Boolean,
  keyword: {
    type: [String, Object],
    required: true
  },
  ideas: Array,
  loading: Boolean,
  languageCode: {
    type: String,
    default: 'en'
  },
  showFaqs: {
    type: Boolean,
    default: false
  },
  showTakeaways: {
    type: Boolean,
    default: false
  },
  numFaqs: {
    type: Number,
    default: 3
  },
  numTakeaways: {
    type: Number,
    default: 3
  },
  contentLength: {
    type: String,
    default: 'medium'
  }
})

const emit = defineEmits([
  'close', 
  'generate', 
  'write',
  'update:showFaqs',
  'update:showTakeaways',
  'update:numFaqs',
  'update:numTakeaways',
  'update:contentLength'
])

const openAIStore = useOpenAIStore()
const writingArticle = ref('')
const selectedContentType = ref('')
const localLoading = ref(false)

// Local state for FAQ and Takeaway controls
const localShowFaqs = ref(props.showFaqs)
const localShowTakeaways = ref(props.showTakeaways)
const localNumFaqs = ref(props.numFaqs)
const localNumTakeaways = ref(props.numTakeaways)
const selectedContentLength = ref(props.contentLength)

// Watch for changes in the local state and emit updates
watch(localShowFaqs, (newVal) => {
  console.log('BlogIdeasPopup - localShowFaqs changed:', newVal)
  emit('update:showFaqs', newVal)
})

watch(localShowTakeaways, (newVal) => {
  console.log('BlogIdeasPopup - localShowTakeaways changed:', newVal)
  emit('update:showTakeaways', newVal)
})

watch(localNumFaqs, (newVal) => {
  console.log('BlogIdeasPopup - localNumFaqs changed:', newVal)
  emit('update:numFaqs', newVal)
})

watch(localNumTakeaways, (newVal) => {
  console.log('BlogIdeasPopup - localNumTakeaways changed:', newVal)
  emit('update:numTakeaways', newVal)
})

// Also watch for prop changes
watch(() => props.showFaqs, (newVal) => {
  console.log('prop showFaqs changed:', newVal)
  localShowFaqs.value = newVal
})

watch(() => props.showTakeaways, (newVal) => {
  console.log('prop showTakeaways changed:', newVal)
  localShowTakeaways.value = newVal
})

watch(() => props.numFaqs, (newVal) => {
  console.log('prop numFaqs changed:', newVal)
  localNumFaqs.value = newVal
})

watch(() => props.numTakeaways, (newVal) => {
  console.log('prop numTakeaways changed:', newVal)
  localNumTakeaways.value = newVal
})

// Watch for changes in content length
watch(selectedContentLength, (newVal) => {
  console.log('BlogIdeasPopup - selectedContentLength changed:', newVal)
  emit('update:contentLength', newVal)
})

// Reset content type when modal is opened/closed
watch(() => props.show, (newValue) => {
  if (!newValue) {
    selectedContentType.value = ''
  }
})

const close = () => {
  selectedContentType.value = ''
  emit('close')
}

// Generate ideas with selected content type
const generateWithContentType = async () => {
  if (!selectedContentType.value) {
    alert('Please select a content type first')
    return
  }
  
  localLoading.value = true
  try {
    // Get language code from keyword, or its seed keyword, or provided language code
    const effectiveLanguageCode = props.keyword.language_code || props.languageCode
    
    await emit('generate', { 
      keyword: props.keyword,
      contentType: selectedContentType.value,
      languageCode: effectiveLanguageCode,
      contentLength: selectedContentLength.value
    })
  } catch (error) {
    console.error('Error generating ideas:', error)
  } finally {
    localLoading.value = false
  }
}

const writeArticle = async (idea) => {
  writingArticle.value = idea.title
  try {
    // Get the keyword term
    const keywordTerm = typeof props.keyword === 'string' ? props.keyword : props.keyword.term
    if (!keywordTerm) {
      throw new Error('Keyword term is required')
    }

    // Get the language code from the idea first, then keyword, then props
    const effectiveLanguageCode = idea.language_code || 
      (typeof props.keyword === 'object' ? props.keyword.language_code : null) || 
      props.languageCode

    if (!effectiveLanguageCode) {
      throw new Error('Language code is required')
    }
    
    // Create a proper search intent object
    const searchIntent = {
      primary: idea.searchIntent || 
        (typeof props.keyword === 'object' ? props.keyword.search_intent_primary : null) || 
        'informational',
      secondary: (typeof props.keyword === 'object' ? props.keyword.search_intent_secondary : []) || []
    }

    const params = {
      keyword: keywordTerm,
      title: idea.title,
      keyPoints: idea.keyPoints || [],
      searchVolume: (typeof props.keyword === 'object' ? props.keyword.searchVolume : null) || 0,
      competition: (typeof props.keyword === 'object' ? props.keyword.difficulty?.competition : null) || 0,
      competitionLevel: (typeof props.keyword === 'object' ? props.keyword.difficulty?.competitionLevelText : null) || 'Unknown',
      seed_keyword: (typeof props.keyword === 'object' ? props.keyword.seed_keyword : null) || keywordTerm,
      searchIntent: searchIntent,
      language_code: effectiveLanguageCode,
      includeFaqs: localShowFaqs.value,
      includeTakeaways: localShowTakeaways.value,
      numFaqs: localShowFaqs.value ? parseInt(localNumFaqs.value, 10) : 0,
      numTakeaways: localShowTakeaways.value ? parseInt(localNumTakeaways.value, 10) : 0,
      contentLength: selectedContentLength.value
    }

    await openAIStore.writeArticle(params)
    close()
  } catch (error) {
    console.error('Error in BlogIdeasPopup writeArticle:', error)
  } finally {
    writingArticle.value = ''
  }
}

// Initialize local state with props
onMounted(() => {
  localShowFaqs.value = props.showFaqs
  localNumFaqs.value = props.numFaqs
  localShowTakeaways.value = props.showTakeaways
  localNumTakeaways.value = props.numTakeaways
  selectedContentLength.value = props.contentLength
  
  console.log('BlogIdeasPopup mounted with initial values:', {
    localShowFaqs: localShowFaqs.value,
    localNumFaqs: localNumFaqs.value,
    localShowTakeaways: localShowTakeaways.value,
    localNumTakeaways: localNumTakeaways.value,
    selectedContentLength: selectedContentLength.value
  })
})
</script>
